/**
 * OverviewView Component
 * Default view when no campaign is selected - shows aggregate metrics and portfolio insights
 */

import React, { useMemo } from 'react';
import {
    TrendingUp,
    TrendingDown,
    DollarSign,
    Target,
    AlertTriangle,
    CheckCircle,
    Award,
    Calendar,
    BarChart3,
    Users,
    Layers
} from 'lucide-react';
import { useCommandCenter } from '../../contexts/CommandCenterContext';
import {
    formatCurrency,
    evaluateCampaignHealth,
    calculateBudgetPacing,
    getCampaignEndStatus,
    HEALTH_STATUS,
    PACING_STATUS,
    normalizeObjective,
    getObjectiveConfig
} from '../../utils/objectiveHelpers';
import { formatNumber, formatPercent, DATE_RANGE_LABELS } from '../../utils/helpers';

const OverviewView = () => {
    const {
        campaigns,
        aggregateMetrics,
        aggregateTrends,
        monthlyBudget,
        previousPeriodData,
        dateRange,
        dateRangePreset,
        selectedPlatform
    } = useCommandCenter();

    // Calculate top and bottom performers by objective
    const performanceAnalysis = useMemo(() => {
        if (!campaigns.length) return { top: [], bottom: [] };

        const evaluated = campaigns.map(c => ({
            ...c,
            health: evaluateCampaignHealth(c)
        })).filter(c => c.health.status !== HEALTH_STATUS.UNKNOWN);

        // Sort by health score
        const sorted = [...evaluated].sort((a, b) => b.health.score - a.health.score);

        return {
            top: sorted.slice(0, 3),
            bottom: sorted.filter(c => c.health.status === HEALTH_STATUS.CRITICAL || c.health.status === HEALTH_STATUS.WARNING).slice(0, 3)
        };
    }, [campaigns]);

    // Group campaigns by objective type
    const campaignsByObjective = useMemo(() => {
        const groups = {};
        campaigns.forEach(c => {
            const objective = normalizeObjective(c.objective);
            const config = getObjectiveConfig(objective);
            const label = config.displayLabel;
            if (!groups[label]) {
                groups[label] = { campaigns: [], totalSpend: 0, icon: config.icon };
            }
            groups[label].campaigns.push(c);
            groups[label].totalSpend += c.currentMetrics?.spend || c.insights?.data?.[0]?.spend || 0;
        });
        return groups;
    }, [campaigns]);

    // Count campaigns ending soon
    const endingSoon = useMemo(() => {
        return campaigns.filter(c => {
            const endStatus = getCampaignEndStatus(c);
            return endStatus.isUrgent && !endStatus.isEnded;
        });
    }, [campaigns]);

    // Count pacing issues
    const pacingIssues = useMemo(() => {
        const issues = { overpacing: [], underpacing: [] };
        campaigns.forEach(c => {
            const pacing = calculateBudgetPacing(c);
            if (pacing.status === PACING_STATUS.OVERPACING) {
                issues.overpacing.push(c);
            } else if (pacing.status === PACING_STATUS.UNDERPACING) {
                issues.underpacing.push(c);
            }
        });
        return issues;
    }, [campaigns]);

    const TrendIndicator = ({ trend, metric }) => {
        if (!trend || trend.direction === 'neutral') return null;
        const isUp = trend.direction === 'up';
        // For spend, up is typically neutral/warning, for revenue/ROAS up is good
        const isPositive = metric === 'spend'
            ? false // Spending more is neutral
            : isUp;

        return (
            <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '2px',
                fontSize: 'var(--font-size-xs)',
                color: trend.color,
                fontWeight: 500
            }}>
                {isUp ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                {trend.percentage.toFixed(1)}%
            </span>
        );
    };

    return (
        <div style={{
            padding: 'var(--space-6)',
            maxWidth: '1000px'
        }}>
            {/* Period Comparison Header */}
            <div style={{
                background: 'var(--color-surface)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--color-border)',
                padding: 'var(--space-5)',
                marginBottom: 'var(--space-6)'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 'var(--space-4)'
                }}>
                    <h2 style={{
                        margin: 0,
                        fontSize: 'var(--font-size-lg)',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)'
                    }}>
                        <Calendar size={20} />
                        Period Summary
                    </h2>
                    <span style={{
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-text-tertiary)'
                    }}>
                        {DATE_RANGE_LABELS[dateRangePreset] || 'Last 7 Days'} vs previous period
                    </span>
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: 'var(--space-4)'
                }}>
                    <MetricCard
                        label="Total Spend"
                        value={formatCurrency(aggregateMetrics?.spend || 0)}
                        trend={aggregateTrends.spend}
                        metric="spend"
                        icon={DollarSign}
                    />
                    <MetricCard
                        label="Revenue"
                        value={formatCurrency(aggregateMetrics?.revenue || 0)}
                        trend={aggregateTrends.revenue}
                        metric="revenue"
                        icon={TrendingUp}
                        positive
                    />
                    <MetricCard
                        label="ROAS"
                        value={`${(aggregateMetrics?.roas || 0).toFixed(2)}x`}
                        trend={aggregateTrends.roas}
                        metric="roas"
                        icon={Target}
                        positive
                    />
                    <MetricCard
                        label="Conversions"
                        value={formatNumber(aggregateMetrics?.conversions || 0)}
                        trend={aggregateTrends.conversions}
                        metric="conversions"
                        icon={CheckCircle}
                        positive
                    />
                </div>
            </div>

            {/* Monthly Budget Tracking */}
            {monthlyBudget && (
                <div style={{
                    background: 'var(--color-surface)',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid var(--color-border)',
                    padding: 'var(--space-5)',
                    marginBottom: 'var(--space-6)'
                }}>
                    <h3 style={{
                        margin: '0 0 var(--space-4) 0',
                        fontSize: 'var(--font-size-md)',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)'
                    }}>
                        <DollarSign size={18} />
                        Monthly Budget
                    </h3>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-4)',
                        marginBottom: 'var(--space-3)'
                    }}>
                        <div style={{ flex: 1 }}>
                            <div style={{
                                height: '8px',
                                background: 'var(--color-border)',
                                borderRadius: 'var(--radius-full)',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    height: '100%',
                                    width: `${Math.min((monthlyBudget.spent / monthlyBudget.total) * 100, 100)}%`,
                                    background: monthlyBudget.spent > monthlyBudget.total
                                        ? 'var(--color-error)'
                                        : 'var(--color-primary)',
                                    borderRadius: 'var(--radius-full)',
                                    transition: 'width var(--transition-base)'
                                }} />
                            </div>
                        </div>
                        <span style={{
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600,
                            color: 'var(--color-text-primary)'
                        }}>
                            {((monthlyBudget.spent / monthlyBudget.total) * 100).toFixed(0)}%
                        </span>
                    </div>

                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-text-secondary)'
                    }}>
                        <span>Spent: {formatCurrency(monthlyBudget.spent)}</span>
                        <span>Remaining: {formatCurrency(Math.max(0, monthlyBudget.total - monthlyBudget.spent))}</span>
                        <span>Total: {formatCurrency(monthlyBudget.total)}</span>
                    </div>
                </div>
            )}

            {/* Two Column Layout */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 'var(--space-6)'
            }}>
                {/* Top Performers */}
                <div style={{
                    background: 'var(--color-surface)',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid var(--color-border)',
                    padding: 'var(--space-5)'
                }}>
                    <h3 style={{
                        margin: '0 0 var(--space-4) 0',
                        fontSize: 'var(--font-size-md)',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        color: 'var(--color-success)'
                    }}>
                        <Award size={18} />
                        Top Performers
                    </h3>

                    {performanceAnalysis.top.length === 0 ? (
                        <p style={{
                            color: 'var(--color-text-tertiary)',
                            fontSize: 'var(--font-size-sm)',
                            margin: 0
                        }}>
                            No campaign data available
                        </p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                            {performanceAnalysis.top.map((campaign, index) => (
                                <PerformerRow
                                    key={campaign.id}
                                    campaign={campaign}
                                    rank={index + 1}
                                    type="top"
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Needs Attention */}
                <div style={{
                    background: 'var(--color-surface)',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid var(--color-border)',
                    padding: 'var(--space-5)'
                }}>
                    <h3 style={{
                        margin: '0 0 var(--space-4) 0',
                        fontSize: 'var(--font-size-md)',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        color: 'var(--color-warning)'
                    }}>
                        <AlertTriangle size={18} />
                        Needs Attention
                    </h3>

                    {performanceAnalysis.bottom.length === 0 ? (
                        <p style={{
                            color: 'var(--color-text-tertiary)',
                            fontSize: 'var(--font-size-sm)',
                            margin: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)'
                        }}>
                            <CheckCircle size={16} style={{ color: 'var(--color-success)' }} />
                            All campaigns performing well
                        </p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                            {performanceAnalysis.bottom.map((campaign, index) => (
                                <PerformerRow
                                    key={campaign.id}
                                    campaign={campaign}
                                    rank={index + 1}
                                    type="bottom"
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Campaigns by Objective */}
            <div style={{
                background: 'var(--color-surface)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--color-border)',
                padding: 'var(--space-5)',
                marginTop: 'var(--space-6)'
            }}>
                <h3 style={{
                    margin: '0 0 var(--space-4) 0',
                    fontSize: 'var(--font-size-md)',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                }}>
                    <BarChart3 size={18} />
                    Campaigns by Objective
                </h3>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: 'var(--space-3)'
                }}>
                    {Object.entries(campaignsByObjective).map(([label, data]) => (
                        <div
                            key={label}
                            style={{
                                padding: 'var(--space-3)',
                                background: 'var(--color-background)',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--color-border)'
                            }}
                        >
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                marginBottom: 'var(--space-2)'
                            }}>
                                <span style={{ fontSize: '1.2em' }}>{data.icon}</span>
                                <span style={{
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 600,
                                    color: 'var(--color-text-primary)'
                                }}>
                                    {label}
                                </span>
                            </div>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                <span>{data.campaigns.length} campaigns</span>
                                <span>{formatCurrency(data.totalSpend)}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Alerts Summary */}
            {(endingSoon.length > 0 || pacingIssues.overpacing.length > 0 || pacingIssues.underpacing.length > 0) && (
                <div style={{
                    background: 'var(--color-surface)',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid var(--color-border)',
                    padding: 'var(--space-5)',
                    marginTop: 'var(--space-6)'
                }}>
                    <h3 style={{
                        margin: '0 0 var(--space-4) 0',
                        fontSize: 'var(--font-size-md)',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)',
                        color: 'var(--color-warning)'
                    }}>
                        <AlertTriangle size={18} />
                        Alerts
                    </h3>

                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 'var(--space-3)'
                    }}>
                        {endingSoon.length > 0 && (
                            <AlertBadge
                                icon={Calendar}
                                label={`${endingSoon.length} campaign${endingSoon.length > 1 ? 's' : ''} ending soon`}
                                color="var(--color-warning)"
                            />
                        )}
                        {pacingIssues.overpacing.length > 0 && (
                            <AlertBadge
                                icon={TrendingUp}
                                label={`${pacingIssues.overpacing.length} overpacing`}
                                color="var(--color-error)"
                            />
                        )}
                        {pacingIssues.underpacing.length > 0 && (
                            <AlertBadge
                                icon={TrendingDown}
                                label={`${pacingIssues.underpacing.length} underpacing`}
                                color="var(--color-warning)"
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Metric Card Sub-component
const MetricCard = ({ label, value, trend, metric, icon: Icon, positive }) => (
    <div style={{
        padding: 'var(--space-3)',
        background: 'var(--color-background)',
        borderRadius: 'var(--radius-md)'
    }}>
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 'var(--space-1)'
        }}>
            <span style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-tertiary)',
                textTransform: 'uppercase'
            }}>
                {label}
            </span>
            <Icon size={14} style={{ color: 'var(--color-text-tertiary)' }} />
        </div>
        <div style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 'var(--space-2)'
        }}>
            <span style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 700,
                color: 'var(--color-text-primary)'
            }}>
                {value}
            </span>
            {trend && trend.direction !== 'neutral' && (
                <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '2px',
                    fontSize: 'var(--font-size-xs)',
                    color: trend.color,
                    fontWeight: 500
                }}>
                    {trend.direction === 'up' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                    {trend.percentage.toFixed(1)}%
                </span>
            )}
        </div>
    </div>
);

// Performer Row Sub-component
const PerformerRow = ({ campaign, rank, type }) => {
    const health = campaign.health;
    const config = getObjectiveConfig(normalizeObjective(campaign.objective));

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: 'var(--space-2)',
            background: 'var(--color-background)',
            borderRadius: 'var(--radius-md)'
        }}>
            <span style={{
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: type === 'top'
                    ? 'var(--color-success-alpha)'
                    : 'var(--color-warning-alpha)',
                color: type === 'top' ? 'var(--color-success)' : 'var(--color-warning)',
                borderRadius: 'var(--radius-full)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 700
            }}>
                {rank}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 500,
                    color: 'var(--color-text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}>
                    {campaign.name}
                </div>
                <div style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-tertiary)'
                }}>
                    {config.icon} {config.displayLabel}
                </div>
            </div>
            <div style={{ textAlign: 'right' }}>
                <div style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    color: health.status === HEALTH_STATUS.EXCELLENT || health.status === HEALTH_STATUS.GOOD
                        ? 'var(--color-success)'
                        : health.status === HEALTH_STATUS.WARNING
                            ? 'var(--color-warning)'
                            : 'var(--color-error)'
                }}>
                    {health.formattedValue}
                </div>
                <div style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-tertiary)'
                }}>
                    {health.message}
                </div>
            </div>
        </div>
    );
};

// Alert Badge Sub-component
const AlertBadge = ({ icon: Icon, label, color }) => (
    <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-2) var(--space-3)',
        background: `${color}15`,
        border: `1px solid ${color}40`,
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--font-size-sm)',
        color
    }}>
        <Icon size={14} />
        {label}
    </div>
);

export default OverviewView;
